import { SpreadsheetActions } from "@/components/tickets/SpreadsheetPreviewDialog";
import { spreadsheetKind } from "@/lib/spreadsheetPreview";
import { FileDropOverlay, useFileDrop } from "@/hooks/useFileDrop";
import { validateAttachment } from "@/lib/attachmentRules";
import { MentionTextarea } from "@/components/common/MentionTextarea";
import { AudioAttachmentPlayer } from "@/components/tickets/AudioAttachmentPlayer";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { agentService } from "@/services/agent.service/agent.api";
import { authService } from "@/services/auth.service/auth.api";
import { callService } from "@/services/call.service/call.api";
import clientService from "@/services/client";
import companyService from "@/services/company";
import {
  type Conversation,
  type ConversationMessage,
  type MessageAttachment,
  conversationService,
} from "@/services/conversation.service/conversation.api";
import { realtimeService } from "@/services/realtime";
import ticketService, { type Ticket } from "@/services/ticket";
import {
  type PlatformTemplate,
  type WhatsAppTemplate,
  whatsappService,
} from "@/services/whatsapp.service/whatsapp.api";
import { linkifyText } from "@/lib/linkify";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  CheckCheck,
  ChevronsUpDown,
  Clock,
  Eye,
  FileText,
  ImageOff,
  LayoutTemplate,
  Link,
  Loader2,
  Mail,
  MessageCircle,
  Mic,
  Paperclip,
  Phone,
  Plus,
  Reply,
  Search,
  Send,
  Square,
  TicketIcon,
  UserPlus,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string) {
  const time = new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formatDate(iso)}, ${time}`;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Hoje";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function isWindowExpired(windowExpiresAt?: string): boolean {
  if (!windowExpiresAt) return false;
  return new Date(windowExpiresAt) < new Date();
}

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Números de variável usados no corpo de um template (ex.: "Olá {{1}}, {{2}}" → [1, 2]). */
function extractVariableNumbers(text: string): number[] {
  const nums = new Set(Array.from((text || "").matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1])));
  return Array.from(nums).sort((a, b) => a - b);
}

function bodyTextOf(t: WhatsAppTemplate): string {
  return t.components?.find((c) => c.type === "BODY")?.text || "";
}

// ─── Message status icon ──────────────────────────────────────────────────────

function MessageStatusIcon({
  status,
  errorReason,
}: {
  status?: ConversationMessage["status"];
  errorReason?: string;
}) {
  if (!status) return null;
  const base = "h-3.5 w-3.5";
  if (status === "read") return <CheckCheck className={cn(base, "text-blue-400")} />;
  if (status === "delivered") return <CheckCheck className={cn(base, "text-muted-foreground")} />;
  if (status === "sent") return <Check className={cn(base, "text-muted-foreground")} />;
  if (status === "failed") {
    return (
      <span title={errorReason || "Falha no envio"}>
        <AlertCircle className={cn(base, "text-destructive")} />
      </span>
    );
  }
  return null;
}

// ─── Attachment renderer ──────────────────────────────────────────────────────

/** Reserva o espaço com um skeleton enquanto a imagem baixa (rede lenta deixava a bolha "vazia"
 * até carregar, só com o ícone de imagem quebrada do navegador) e mostra um fallback se falhar. */
function ImageAttachment({ url, name }: { url: string; name?: string }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-1 flex h-24 w-[240px] flex-col items-center justify-center gap-1 rounded-lg border border-border bg-muted text-xs text-muted-foreground hover:bg-accent"
      >
        <ImageOff className="h-5 w-5" />
        <span>Não foi possível carregar a imagem</span>
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="relative mt-1 block w-[240px]">
      {!loaded && <Skeleton className="absolute inset-0 h-40 w-[240px] rounded-lg" />}
      {/* display:none (a classe "hidden") tira a caixa de layout do elemento — sem tamanho, o
          navegador nunca considera a imagem "perto da viewport" e o loading="lazy" trava pra
          sempre. Por isso escondemos com opacidade (mantém a caixa) em vez de display. */}
      <img
        src={url}
        alt={name || "Imagem"}
        loading="lazy"
        className={cn(
          "max-w-[240px] rounded-lg border border-border transition-opacity",
          loaded ? "opacity-100" : "h-40 w-[240px] opacity-0"
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </a>
  );
}

function AttachmentRenderer({ attachment }: { attachment: ConversationMessage["content"]["attachments"] extends (infer A)[] | undefined ? NonNullable<A> : never }) {
  const isAudio = attachment.mimeType.startsWith("audio/");
  const isImage = attachment.mimeType.startsWith("image/");
  const isVideo = attachment.mimeType.startsWith("video/");

  if (isAudio) {
    // Mensagens antigas podem ter o nome do arquivo com o mimeType grudado (ex.: "audio.ogg; codecs=opus").
    const audioLabel = (attachment.name || "Áudio").split(";")[0].trim();
    return (
      <div className="mt-1 space-y-1.5">
        <AudioAttachmentPlayer src={attachment.url} label={audioLabel} compact />
        {attachment.transcript && (
          <div className="rounded-md border border-border/40 bg-muted/20 px-2 py-1.5">
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
              {attachment.transcript}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (isImage) {
    return <ImageAttachment url={attachment.url} name={attachment.name} />;
  }

  if (isVideo) {
    return (
      <video
        src={attachment.url}
        controls
        className="mt-1 max-w-[240px] rounded-lg border border-border"
      />
    );
  }

  if (spreadsheetKind(attachment)) {
    return (
      <div className="mt-1 space-y-1 rounded-md border border-border bg-muted px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 shrink-0" />
          <span className="min-w-0 truncate">{attachment.name || "Planilha"}</span>
        </div>
        <SpreadsheetActions attachment={attachment} className="flex gap-2" />
      </div>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="mt-1 flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm hover:bg-accent"
    >
      <Paperclip className="h-4 w-4 shrink-0" />
      <span className="min-w-0 truncate">{attachment.name || "Arquivo"}</span>
    </a>
  );
}

// ─── Selects (cliente / ticket) ───────────────────────────────────────────────

function ClientCombobox({
  value,
  onChange,
  companyId,
  disabled,
  disabledPlaceholder,
}: {
  value?: string;
  onChange: (id: string | undefined, name?: string) => void;
  /** Filtra a busca pra Pessoas dessa Empresa. */
  companyId?: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<{ id: string; label: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  useEffect(() => {
    if (!open || disabled) return;
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      const [res] = await clientService.getAll(query, 1, 20, undefined, companyId);
      if (!active) return;
      setOptions(
        (res?.result || []).map((c) => ({
          id: c._id,
          label: c.phone ? `${c.name} · ${c.phone}` : c.name,
          name: c.name,
        }))
      );
      setLoading(false);
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, open, companyId, disabled]);

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedLabel || (disabled ? disabledPlaceholder : undefined) || "Selecione o cliente"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar cliente..." value={query} onValueChange={setQuery} />
          <CommandList>
            {loading && <div className="py-4 text-center text-sm text-muted-foreground">Buscando...</div>}
            {!loading && <CommandEmpty>Nenhum cliente encontrado</CommandEmpty>}
            <CommandGroup>
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(undefined, undefined);
                    setSelectedLabel("");
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  Limpar seleção
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.id}
                  onSelect={() => {
                    onChange(opt.id, opt.name);
                    setSelectedLabel(opt.label);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ClientMultiCombobox({
  values,
  onChange,
  companyId,
  disabled,
  disabledPlaceholder,
}: {
  values: string[];
  onChange: (ids: string[]) => void;
  /** Filtra a busca pra Pessoas dessa Empresa. */
  companyId?: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<{ id: string; label: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  // Mantém os rótulos das pessoas já selecionadas mesmo que saiam da busca atual.
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || disabled) return;
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      const [res] = await clientService.getAll(query, 1, 20, undefined, companyId);
      if (!active) return;
      const opts = (res?.result || []).map((c) => ({
        id: c._id,
        label: c.phone ? `${c.name} · ${c.phone}` : c.name,
        name: c.name,
      }));
      setOptions(opts);
      setLabels((prev) => {
        const next = { ...prev };
        for (const o of opts) next[o.id] = o.label;
        return next;
      });
      setLoading(false);
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, open, companyId, disabled]);

  function toggle(id: string) {
    if (values.includes(id)) {
      onChange(values.filter((v) => v !== id));
    } else {
      onChange([...values, id]);
    }
  }

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1 font-normal">
              {labels[id] || id}
              <button
                type="button"
                onClick={() => onChange(values.filter((v) => v !== id))}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={disabled ? false : open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            className="w-full justify-between font-normal text-muted-foreground"
          >
            <span className="truncate">
              {disabled ? disabledPlaceholder : "Adicionar pessoa..."}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Buscar pessoa..." value={query} onValueChange={setQuery} />
            <CommandList>
              {loading && <div className="py-4 text-center text-sm text-muted-foreground">Buscando...</div>}
              {!loading && <CommandEmpty>Nenhuma pessoa encontrada</CommandEmpty>}
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem key={opt.id} value={opt.id} onSelect={() => toggle(opt.id)}>
                    <Checkbox checked={values.includes(opt.id)} className="mr-2" />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TicketCombobox({
  value,
  onChange,
}: {
  value?: string;
  onChange: (ticket: { ref: string; label: string } | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  useEffect(() => {
    if (!open || tickets.length) return;
    setLoading(true);
    ticketService.getAll({ limit: 100 }).then(([res]) => {
      setTickets(res?.result || []);
      setLoading(false);
    });
  }, [open, tickets.length]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{selectedLabel || "Selecione o ticket"}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Buscar por protocolo ou título..." />
          <CommandList>
            {loading && <div className="py-4 text-center text-sm text-muted-foreground">Carregando...</div>}
            {!loading && <CommandEmpty>Nenhum ticket encontrado</CommandEmpty>}
            <CommandGroup>
              {tickets.map((t) => {
                const label = `#${t.protocolId} — ${t.title}`;
                return (
                  <CommandItem
                    key={t._id}
                    value={label}
                    onSelect={() => {
                      onChange({ ref: t.protocolId || t._id, label });
                      setSelectedLabel(label);
                      setOpen(false);
                    }}
                  >
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Create Ticket modal ──────────────────────────────────────────────────────

const createTicketSchema = z.object({
  title: z.string().optional(),
  priority: z.enum(["low", "medium", "high", "urgent"]).optional(),
  clientId: z.string().optional(),
  clientUserIds: z.array(z.string()).optional(),
});

type CreateTicketForm = z.infer<typeof createTicketSchema>;

function CreateTicketModal({
  open,
  onClose,
  conversationId,
  messageIds,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  messageIds: string[];
  onSuccess: (ticketId: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [companies, setCompanies] = useState<{ _id: string; name: string }[]>([]);
  const form = useForm<CreateTicketForm>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      title: "",
      priority: undefined,
      clientId: "",
      clientUserIds: [],
    },
  });

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const [data] = await companyService.getAll(undefined, 1, 500);
      setCompanies(data?.result || []);
    })();
  }, [open]);

  const clientId = form.watch("clientId");

  async function onSubmit(values: CreateTicketForm) {
    setSubmitting(true);
    const [primaryContactId, ...extraMemberIds] = values.clientUserIds || [];
    const [res, err] = await conversationService.createTicket(conversationId, {
      title: values.title || undefined,
      priority: values.priority,
      clientId: values.clientId || undefined,
      clientUserId: primaryContactId || undefined,
      memberIds: extraMemberIds.length ? extraMemberIds : undefined,
      messageIds,
    });
    setSubmitting(false);

    if (err || !res?.success) {
      toast.error("Erro ao criar ticket");
      return;
    }

    toast.success("Ticket criado com sucesso!");
    onClose();
    onSuccess(res.ticket._id);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Criar ticket deste trecho</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {messageIds.length} mensagem(ns) selecionada(s)
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título (opcional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Título do ticket" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridade (opcional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a prioridade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa (opcional)</FormLabel>
                  <Select
                    onValueChange={(v) => {
                      field.onChange(v);
                      form.setValue("clientUserIds", []);
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c._id} value={c._id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clientUserIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pessoa (opcional, pode selecionar mais de uma)</FormLabel>
                  <FormControl>
                    <ClientMultiCombobox
                      values={field.value || []}
                      onChange={field.onChange}
                      companyId={clientId || undefined}
                      disabled={!clientId}
                      disabledPlaceholder="Selecione uma empresa primeiro"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Criar ticket
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Link Ticket modal ────────────────────────────────────────────────────────

function LinkTicketModal({
  open,
  onClose,
  conversationId,
  messageIds,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  messageIds: string[];
}) {
  const [ticketRef, setTicketRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleLink() {
    if (!ticketRef.trim()) {
      toast.error("Informe o protocolo ou ID do ticket");
      return;
    }
    setSubmitting(true);
    const [res, err] = await conversationService.linkTicket(conversationId, {
      ticketRef: ticketRef.trim(),
      messageIds,
    });
    setSubmitting(false);

    if (err || !res?.success) {
      toast.error("Erro ao vincular ticket");
      return;
    }

    toast.success("Mensagens vinculadas ao ticket!");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Vincular a ticket existente</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {messageIds.length} mensagem(ns) selecionada(s)
        </p>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Ticket</label>
            <TicketCombobox
              value={ticketRef}
              onChange={(t) => setTicketRef(t?.ref || "")}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleLink} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Vincular
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── New conversation modal ───────────────────────────────────────────────────

const newConversationSchema = z
  .object({
    clientUserId: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine((v) => !v || /^\d{10,15}$/.test(v.replace(/\D/g, "")), {
        message:
          "Telefone inválido — informe DDI + DDD + número (ex.: 5511999999999)",
      }),
    name: z.string().optional(),
    templateName: z.string().min(1, "Selecione um template"),
  })
  .refine((data) => !!data.clientUserId || !!data.phone?.trim(), {
    message: "Selecione uma pessoa ou informe um telefone",
    path: ["phone"],
  });

type NewConversationForm = z.infer<typeof newConversationSchema>;

function NewConversationModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (conversation: Conversation) => void;
}) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [clientName, setClientName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<NewConversationForm>({
    resolver: zodResolver(newConversationSchema),
    defaultValues: { clientUserId: "", phone: "", name: "", templateName: "" },
  });

  useEffect(() => {
    if (!open) return;
    void (async () => {
      setLoadingTemplates(true);
      const [data] = await whatsappService.listTemplates();
      setLoadingTemplates(false);
      setTemplates((data || []).filter((t) => t.status === "APPROVED"));
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      form.reset({ clientUserId: "", phone: "", name: "", templateName: "" });
      setVariables({});
      setClientName("");
    }
  }, [open, form]);

  const clientUserId = form.watch("clientUserId");
  const manualName = form.watch("name");
  const templateName = form.watch("templateName");
  const selectedTemplate = templates.find((t) => t.name === templateName);
  const variableNumbers = selectedTemplate
    ? extractVariableNumbers(bodyTextOf(selectedTemplate))
    : [];

  // {{1}} segue a convenção do app inteiro (ver pushTicketInteraction): sempre nome do cliente —
  // pré-preenche com o nome já conhecido (Pessoa selecionada ou digitado), mas continua editável.
  function defaultVariableValue(n: number): string {
    return n === 1 ? clientName || manualName || "" : "";
  }

  async function onSubmit(values: NewConversationForm) {
    if (!selectedTemplate) return;
    setSubmitting(true);
    const components = variableNumbers.length
      ? [
          {
            type: "body",
            parameters: variableNumbers.map((n) => ({
              type: "text",
              text: variables[String(n)] ?? defaultVariableValue(n),
            })),
          },
        ]
      : undefined;

    const [res, err] = await conversationService.start({
      clientUserId: values.clientUserId || undefined,
      phone: values.clientUserId ? undefined : values.phone?.replace(/\D/g, ""),
      name: values.name || undefined,
      templateName: selectedTemplate.name,
      language: selectedTemplate.language,
      components,
    });
    setSubmitting(false);

    if (err || !res?.success) {
      toast.error(err?.msg || "Erro ao iniciar conversa — verifique o telefone e tente novamente");
      return;
    }

    // O envio à Graph API foi aceito, mas a confirmação real de entrega chega depois, via webhook
    // assíncrono — o ícone de status na conversa (✓ enviado, ✓✓ entregue, ou alerta de falha) reflete isso.
    toast.success("Template enviado! Acompanhe a confirmação de entrega na conversa.");
    onClose();
    onSuccess(res.conversation);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova conversa</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Fora da janela de 24h, o primeiro contato com o cliente só pode ser feito com um template
          aprovado.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="clientUserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pessoa cadastrada</FormLabel>
                  <FormControl>
                    <ClientCombobox
                      value={field.value}
                      onChange={(id, name) => {
                        field.onChange(id);
                        setClientName(name || "");
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!clientUserId && (
              <>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone (com DDI e DDD)</FormLabel>
                      <FormControl>
                        <Input placeholder="5511999999999" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome (opcional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Nome do contato" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="templateName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template</FormLabel>
                  {loadingTemplates ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="text-sm text-amber-800">
                      Nenhum template aprovado encontrado — crie um em Configurações → WhatsApp.
                    </p>
                  ) : (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Escolha um template" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.name} value={t.name}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedTemplate && (
              <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">
                {bodyTextOf(selectedTemplate)}
              </p>
            )}
            {variableNumbers.map((n) => (
              <Input
                key={n}
                placeholder={`Valor de {{${n}}}`}
                value={variables[String(n)] ?? defaultVariableValue(n)}
                onChange={(e) => setVariables((v) => ({ ...v, [String(n)]: e.target.value }))}
              />
            ))}

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting || !selectedTemplate} className="gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Iniciar conversa
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Conversation list item ───────────────────────────────────────────────────

function ConversationItem({
  conversation,
  selected,
  onClick,
}: {
  conversation: Conversation;
  selected: boolean;
  onClick: () => void;
}) {
  const expired = isWindowExpired(conversation.windowExpiresAt);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50",
        selected && "bg-accent"
      )}
    >
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarFallback className="bg-green-100 text-green-700 text-sm">
          {getInitials(conversation.name || conversation.waId)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1">
          <span className="truncate font-medium text-sm">
            {conversation.name || conversation.waId || "Contato"}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {conversation.lastMessageAt ? formatDate(conversation.lastMessageAt) : ""}
          </span>
        </div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="truncate text-xs text-muted-foreground">
            {conversation.lastMessagePreview || "Sem mensagens"}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {expired && (
              <span title="Janela expirada">
                <Clock className="h-3 w-3 text-amber-500" />
              </span>
            )}
            {(conversation.unreadCount ?? 0) > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-green-500 px-1 text-[10px] font-bold text-white">
                {conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────

/**
 * `readBy` é um watermark por agente (última vez que abriu a conversa) — não um registro por
 * mensagem. Deriva "visto até aqui" achando, para cada agente, a mensagem mais recente com
 * `createdAt` <= o `readAt` dele, e agrupando os nomes nessa mensagem (mesmo princípio do
 * `agentLastReadAt`/`contactLastReadAt` de Tickets, aplicado a N agentes em vez de 2 lados fixos).
 */
function computeSeenByMessageId(
  messages: ConversationMessage[],
  readBy: Array<{ agentId: string; readAt: string | Date }> | undefined,
  orgAgents: Array<{ _id: string; name: string }>,
  loggedUserId?: string
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  if (!messages.length || !readBy?.length) return result;
  for (const entry of readBy) {
    const agent = orgAgents.find((a) => a._id === entry.agentId);
    if (!agent) continue;
    const readAtMs = new Date(entry.readAt).getTime();
    let target: ConversationMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (new Date(messages[i].createdAt).getTime() <= readAtMs) {
        target = messages[i];
        break;
      }
    }
    if (!target) continue;
    const name = entry.agentId === loggedUserId ? "Você" : agent.name;
    const list = result.get(target._id) || [];
    list.push(name);
    result.set(target._id, list);
  }
  return result;
}

function MessageBubble({
  message,
  quoted,
  selected,
  selectionMode,
  onToggleSelect,
  onReply,
  agentName,
  seenBy,
}: {
  message: ConversationMessage;
  quoted?: ConversationMessage;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect: (id: string) => void;
  onReply: (message: ConversationMessage) => void;
  agentName?: string;
  seenBy?: string[];
}) {
  const isOutbound = message.direction === "outbound";
  const isInternal = !!message.isInternal;

  return (
    <div
      className={cn(
        "group flex items-start gap-2",
        isInternal ? "flex-row" : isOutbound ? "flex-row-reverse" : "flex-row"
      )}
    >
      {selectionMode && (
        <div className="mt-2 shrink-0">
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(message._id)}
            className="h-4 w-4"
          />
        </div>
      )}
      <div
        className={cn(
          // largura máxima em px fixo (não percentual) — max-w-[75%] dependia do container pai
          // ter uma largura definida pra resolver, e isso não estava sendo respeitado de forma
          // confiável, deixando mensagens longas estourarem a tela em vez de quebrar linha.
          "relative w-fit max-w-[280px] sm:max-w-[420px] md:max-w-[520px] min-w-0 rounded-2xl px-4 py-2 shadow-sm",
          isInternal
            ? "w-full max-w-full rounded-lg border border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-50"
            : isOutbound
              ? "rounded-tr-sm bg-primary text-primary-foreground"
              : "rounded-tl-sm bg-card border border-border"
        )}
      >
        {isInternal && (
          <p className="mb-1 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            🔒 Nota interna{agentName ? ` · ${agentName}` : ""}
          </p>
        )}
        {message.contextWamid && (
          <div
            className={cn(
              "mb-1.5 rounded-md border-l-2 px-2 py-1 text-xs opacity-80",
              isOutbound ? "border-primary-foreground/40 bg-black/10" : "border-primary/40 bg-muted"
            )}
          >
            <p className="whitespace-pre-wrap break-words line-clamp-3">
              {quoted ? quoted.content.msg || "(anexo)" : "Mensagem citada"}
            </p>
          </div>
        )}
        {message.content.msg && (
          <p className="whitespace-pre-wrap break-words text-sm">{linkifyText(message.content.msg)}</p>
        )}
        {message.content.attachments?.map((att, i) => (
          <AttachmentRenderer key={i} attachment={att} />
        ))}
        {message.reactions && message.reactions.length > 0 && (
          <div
            className={cn(
              "absolute -bottom-2.5 flex items-center gap-0.5 rounded-full border border-border bg-card px-1 py-0.5 text-xs leading-none shadow-sm",
              isOutbound ? "right-2" : "left-2"
            )}
          >
            {message.reactions.map((r, i) => (
              <span key={i}>{r.emoji}</span>
            ))}
          </div>
        )}
        <div
          className={cn(
            "mt-1 flex items-center gap-1 text-[10px]",
            isOutbound ? "justify-end text-primary-foreground/70" : "text-muted-foreground"
          )}
        >
          <span>{formatTime(message.createdAt)}</span>
          {isOutbound && message._id.startsWith("temp-") && (
            <Loader2 className="h-3 w-3 animate-spin" />
          )}
          {isOutbound && !message._id.startsWith("temp-") && (
            <MessageStatusIcon status={message.status} errorReason={message.errorReason} />
          )}
          {message.linkedTicketId && (
            <span title="Vinculado a ticket">
              <TicketIcon className="h-3 w-3 ml-1" />
            </span>
          )}
        </div>
        {!!seenBy?.length && (
          <div
            className={cn(
              "mt-0.5 flex items-center gap-1 text-[10px]",
              isOutbound ? "justify-end text-primary-foreground/70" : "text-muted-foreground"
            )}
          >
            <Eye className="h-2.5 w-2.5 shrink-0" />
            <span>Visto por {seenBy.join(", ")}</span>
          </div>
        )}
      </div>
      {!selectionMode && (
        <button
          type="button"
          onClick={() => onReply(message)}
          className="mt-2 shrink-0 rounded-full p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100"
          aria-label="Responder"
          title="Responder"
        >
          <Reply className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── Template picker (janela de 24h fechada) ──────────────────────────────────

function TemplatePicker({
  conversationId,
  customerName,
  expired,
  onClose,
  onSent,
}: {
  conversationId: string;
  customerName?: string;
  expired?: boolean;
  onClose?: () => void;
  onSent: (message: ConversationMessage) => void;
}) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [platformTemplates, setPlatformTemplates] = useState<PlatformTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // "meta:<name>" ou "platform:<id>"
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const [data] = await whatsappService.listTemplates();
      setTemplates((data || []).filter((t) => t.status === "APPROVED"));
      // Templates de plataforma (texto livre) só fazem sentido com a janela aberta.
      if (!expired) {
        const [platData] = await whatsappService.listPlatformTemplates();
        setPlatformTemplates((platData || []).filter((t) => t.active));
      }
      setLoading(false);
    })();
  }, [expired]);

  const isPlatform = selectedKey.startsWith("platform:");
  const selectedMeta = !isPlatform ? templates.find((t) => `meta:${t.name}` === selectedKey) : undefined;
  const selectedPlatform = isPlatform
    ? platformTemplates.find((t) => `platform:${t._id}` === selectedKey)
    : undefined;
  const variableNumbers = selectedMeta ? extractVariableNumbers(bodyTextOf(selectedMeta)) : [];

  // {{1}} segue a convenção do app inteiro (ver pushTicketInteraction): sempre nome do cliente.
  function defaultVariableValue(n: number): string {
    return n === 1 ? customerName || "" : "";
  }

  async function handleSend() {
    if (!selectedMeta && !selectedPlatform) return;
    setSending(true);

    if (selectedPlatform) {
      const [res, err] = await conversationService.sendPlatformTemplate(conversationId, selectedPlatform._id);
      setSending(false);
      if (err || !res?.result) {
        toast.error(err?.msg || "Erro ao enviar template");
        return;
      }
      if (res.warning) toast.warning(res.warning);
      toast.success("Mensagem enviada!");
      onSent(res.result);
      setSelectedKey("");
      if (!expired) onClose?.();
      return;
    }

    const components = variableNumbers.length
      ? [
          {
            type: "body",
            parameters: variableNumbers.map((n) => ({
              type: "text",
              text: variables[String(n)] ?? defaultVariableValue(n),
            })),
          },
        ]
      : undefined;

    const [res, err] = await conversationService.sendTemplate(conversationId, {
      templateName: selectedMeta.name,
      language: selectedMeta.language,
      components,
    });
    setSending(false);

    if (err || !res?.result) {
      toast.error(err?.msg || "Erro ao enviar template");
      return;
    }

    toast.success("Template enviado!");
    onSent(res.result);
    setSelectedKey("");
    setVariables({});
    if (!expired) onClose?.();
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>Carregando templates...</span>
      </div>
    );
  }

  if (templates.length === 0 && platformTemplates.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
        <Clock className="h-4 w-4 shrink-0" />
        <span>Nenhum template aprovado encontrado — crie um em Configurações → WhatsApp.</span>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
      <div className="flex items-center justify-between gap-1.5 text-xs font-medium text-amber-800">
        <span className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {expired ? "Janela de 24h expirada — só é possível enviar um template" : "Enviar um template"}
        </span>
        {!expired && onClose && (
          <button type="button" onClick={onClose} className="shrink-0 text-amber-800 hover:text-amber-950">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={selectedKey} onValueChange={setSelectedKey}>
          <SelectTrigger className="bg-background">
            <SelectValue placeholder="Escolha um template" />
          </SelectTrigger>
          <SelectContent>
            {templates.length > 0 && (
              <SelectGroup>
                <SelectLabel>Templates da Meta</SelectLabel>
                {templates.map((t) => (
                  <SelectItem key={t.name} value={`meta:${t.name}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {platformTemplates.length > 0 && (
              <SelectGroup>
                <SelectLabel>Templates de plataforma</SelectLabel>
                {platformTemplates.map((t) => (
                  <SelectItem key={t._id} value={`platform:${t._id}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="shrink-0 gap-2"
          disabled={(!selectedMeta && !selectedPlatform) || sending}
          onClick={() => void handleSend()}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Enviar
        </Button>
      </div>
      {selectedMeta && (
        <p className="rounded-md bg-background px-2 py-1.5 text-xs text-muted-foreground">
          {bodyTextOf(selectedMeta)}
        </p>
      )}
      {selectedPlatform && (
        <p className="rounded-md bg-background px-2 py-1.5 text-xs text-muted-foreground">
          {selectedPlatform.body}
        </p>
      )}
      {variableNumbers.map((n) => (
        <Input
          key={n}
          placeholder={`Valor de {{${n}}}`}
          value={variables[String(n)] ?? defaultVariableValue(n)}
          onChange={(e) => setVariables((v) => ({ ...v, [String(n)]: e.target.value }))}
          className="bg-background"
        />
      ))}
    </div>
  );
}

// ─── Thread panel ─────────────────────────────────────────────────────────────

function ThreadPanel({
  conversation,
  onBack,
}: {
  conversation: Conversation;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const [contact, setContact] = useState(conversation.contact ?? null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ConversationMessage | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFileUrl, setPendingFileUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [manualTemplateOpen, setManualTemplateOpen] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startingCall, setStartingCall] = useState(false);
  const [sendingClaimLink, setSendingClaimLink] = useState(false);
  const [readBy, setReadBy] = useState<Conversation["readBy"]>(conversation.readBy || []);
  const [messageType, setMessageType] = useState<"public" | "internal">("public");
  const [orgAgents, setOrgAgents] = useState<Array<{ _id: string; name: string }>>([]);
  const loggedUserId = authService.getCurrentUser()?._id;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStreamRef = useRef<MediaStream | null>(null);
  const expired = isWindowExpired(conversation.windowExpiresAt);
  // Nota interna não depende da janela de 24h — escrever uma não pode ficar bloqueado atrás do
  // template picker só porque a conversa com o cliente está "fechada".
  const showTemplatePicker = (expired && messageType !== "internal") || manualTemplateOpen;
  const messagesByWamid = new Map(messages.filter((m) => m.wamid).map((m) => [m.wamid as string, m]));
  const seenByMessageId = useMemo(
    () => computeSeenByMessageId(messages, readBy, orgAgents, loggedUserId),
    [messages, readBy, orgAgents, loggedUserId]
  );
  const MESSAGES_PAGE_SIZE = 30;

  function scrollToBottom(behavior: ScrollBehavior = "smooth") {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }

  // Cresce junto com o texto até um teto (bate com o max-h-32 da classe), depois rola por dentro.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [text]);

  useEffect(() => {
    if (!pendingFile) {
      setPendingFileUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(pendingFile);
    setPendingFileUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pendingFile]);

  // Selecionar uma conversa na lista usa o item já carregado (sem `contact` — só o endpoint de
  // detalhe resolve o telefone pra Pessoa); busca à parte pra sempre ter os dados do contato,
  // independente de como a conversa foi aberta (lista, deep-link, ou realtime).
  useEffect(() => {
    setContact(conversation.contact ?? null);
    if (conversation.contact !== undefined) return;
    let cancelled = false;
    void conversationService.getById(conversation._id).then(([data]) => {
      if (!cancelled && data) setContact(data.contact ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [conversation._id, conversation.contact]);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    const [data] = await conversationService.getMessages(conversation._id, {
      limit: MESSAGES_PAGE_SIZE,
    });
    setLoading(false);
    if (data?.result) {
      setMessages(data.result);
      setHasMoreMessages(data.result.length >= MESSAGES_PAGE_SIZE);
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  }, [conversation._id]);

  // Rola pro topo da lista → busca a página anterior (mensagens mais antigas) e prepende,
  // preservando a posição de scroll (senão a tela "pula" pro topo a cada carregamento).
  async function loadOlderMessages() {
    if (loadingMore || !hasMoreMessages || messages.length === 0) return;
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    setLoadingMore(true);
    const [data] = await conversationService.getMessages(conversation._id, {
      limit: MESSAGES_PAGE_SIZE,
      before: messages[0].createdAt,
    });
    setLoadingMore(false);
    if (!data?.result) return;
    setHasMoreMessages(data.result.length >= MESSAGES_PAGE_SIZE);
    if (data.result.length === 0) return;
    setMessages((prev) => [...data.result, ...prev]);
    requestAnimationFrame(() => {
      if (container) container.scrollTop = container.scrollHeight - prevScrollHeight;
    });
  }

  function handleMessagesScroll() {
    if (messagesContainerRef.current && messagesContainerRef.current.scrollTop < 80) {
      void loadOlderMessages();
    }
  }

  useEffect(() => {
    setMessages([]);
    setSelectionMode(false);
    setSelectedIds(new Set());
    setHasMoreMessages(true);
    setManualTemplateOpen(false);
    setReadBy(conversation.readBy || []);
    void fetchMessages();
    void conversationService.markRead(conversation._id).then(([data]) => {
      if (data?.readBy) setReadBy(data.readBy);
    });
  }, [conversation._id, fetchMessages]);

  // Roster da organização pra saber quem AINDA não viu (readBy só lista quem já viu) — carrega
  // uma vez só, não muda entre conversas.
  useEffect(() => {
    void agentService.getAll({}, undefined, undefined).then(([data]) => {
      if (data?.result) setOrgAgents(data.result.map((a: any) => ({ _id: a._id, name: a.name })));
    });
  }, []);

  // Real-time message subscription
  useEffect(() => {
    const unsub = realtimeService.subscribe("conversation:message", (ev) => {
      if (ev.conversationId !== conversation._id) return;
      setMessages((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m._id === ev.message._id)) return prev;
        return [...prev, ev.message];
      });
      requestAnimationFrame(() => scrollToBottom());
      // markRead só corre 1x no mount — sem isso, o unreadCount no backend volta a subir a
      // cada mensagem recebida enquanto a conversa permanece aberta na tela.
      if (ev.message.direction === "inbound") {
        void conversationService.markRead(conversation._id).then(([data]) => {
          if (data?.readBy) setReadBy(data.readBy);
        });
      }
    });

    const unsubStatus = realtimeService.subscribe("message:status", (ev) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.wamid === ev.wamid
            ? { ...m, status: ev.status, errorReason: ev.errorReason, errorCode: ev.errorCode }
            : m
        )
      );
    });

    const unsubReaction = realtimeService.subscribe("conversation:reaction", (ev) => {
      if (ev.conversationId !== conversation._id) return;
      setMessages((prev) =>
        prev.map((m) => (m.wamid === ev.wamid ? { ...m, reactions: ev.reactions } : m))
      );
    });

    // Outro agente (ou eu, em outra aba) marcou como lida — atualiza "visto por" sem precisar
    // reabrir a conversa ou dar F5.
    const unsubRead = realtimeService.subscribe("conversation:read", (ev) => {
      if (ev.conversationId !== conversation._id) return;
      setReadBy(ev.readBy || []);
    });

    return () => {
      unsub();
      unsubStatus();
      unsubReaction();
      unsubRead();
    };
  }, [conversation._id]);

  // Fallback de polling: se o socket perder um evento (rede instável, aba em segundo
  // plano), busca as últimas mensagens, mescla as que faltam e sincroniza status das
  // que já estavam na tela — sem isso só aparecia dando F5.
  const pollLatestMessages = useCallback(async () => {
    const [data] = await conversationService.getMessages(conversation._id, {
      limit: MESSAGES_PAGE_SIZE,
    });
    if (!data?.result) return;
    const latestById = new Map(data.result.map((m) => [m._id, m]));
    setMessages((prev) => {
      let appended = false;
      const merged = prev.map((m) => {
        const fresh = latestById.get(m._id);
        latestById.delete(m._id);
        return fresh ? { ...m, ...fresh } : m;
      });
      if (latestById.size > 0) {
        appended = true;
        merged.push(...latestById.values());
        merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      }
      if (appended) requestAnimationFrame(() => scrollToBottom());
      return merged;
    });
  }, [conversation._id]);

  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === "visible") void pollLatestMessages();
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") void pollLatestMessages();
    }, 15000);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [pollLatestMessages]);

  // Libera o microfone se o painel for desmontado no meio de uma gravação.
  useEffect(() => {
    return () => {
      recordStreamRef.current?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);

  async function handleStartCall() {
    setStartingCall(true);
    const [data, err] = await callService.create({
      conversationId: conversation._id,
    });
    setStartingCall(false);

    if (err || !data) {
      toast.error(err?.msg || "Erro ao iniciar a chamada");
      return;
    }
    // Estilo Meet — a chamada (e o acesso remoto, se precisar) acontece numa aba própria,
    // nunca inline na conversa: ambos só fazem sentido no momento em que estão rolando.
    window.open(`/atendimento/${data._id}`, "_blank");
  }

  async function handleSendClaimLink() {
    setSendingClaimLink(true);
    const [res, err] = await conversationService.sendClaimLink(conversation._id);
    setSendingClaimLink(false);

    if (err || !res) {
      toast.error(err?.msg || "Erro ao enviar o link de cadastro");
      return;
    }
    toast.success(res.msg || "Link de cadastro enviado pelo WhatsApp");
  }

  async function handleSend() {
    const isInternal = messageType === "internal";
    if ((!text.trim() && !pendingFile) || (expired && !isInternal) || sending) return;
    const msg = text.trim();
    const file = pendingFile;
    const contextWamid = replyingTo?.wamid;
    setSending(true);

    // Prefixa com o nome do agente — mesma regra do backend (send() em conversations.service.ts),
    // só pra a mensagem otimista já nascer igual à que vai chegar de volta do servidor, sem "pular"
    // o texto assim que a resposta real chega. Nota interna não leva prefixo — o cabeçalho "Nota
    // interna · Nome" já identifica o autor.
    const agentName = authService.getCurrentUser()?.name?.trim();
    const displayMsg = !isInternal && msg && agentName ? `${agentName}: ${msg}` : msg;

    // Mostra a mensagem na hora (otimista) — o envio de verdade passa pela API do WhatsApp e
    // pode levar alguns segundos; sem isso a tela parecia travada entre o clique e a mensagem
    // aparecer. Some quando a resposta real do servidor chega (ou volta se der erro).
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: ConversationMessage = {
      _id: tempId,
      conversationId: conversation._id,
      direction: "outbound",
      senderAgentId: loggedUserId,
      isInternal,
      content: {
        msg: displayMsg || undefined,
        attachments: file
          ? [{ url: pendingFileUrl || "", name: file.name, mimeType: file.type, size: file.size }]
          : undefined,
      },
      status: "sent",
      contextWamid,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMessage]);
    requestAnimationFrame(() => scrollToBottom());

    let attachments: MessageAttachment[] | undefined;
    if (file) {
      const [ref, err] = await conversationService.uploadAttachment(file);
      if (err || !ref) {
        toast.error(err?.msg || "Falha no upload do arquivo");
        setSending(false);
        setMessages((prev) => prev.filter((m) => m._id !== tempId));
        return;
      }
      attachments = [ref];
    }

    setText("");
    setPendingFile(null);
    setReplyingTo(null);

    const [res, err] = await conversationService.send(conversation._id, {
      msg,
      attachments,
      contextWamid,
      internal: isInternal || undefined,
    });
    setSending(false);

    if (err) {
      const code = (err as { code?: string }).code;
      if (code === "whatsapp-window-closed") {
        toast.error("Janela de 24h expirada. Use um template para reabrir.");
      } else {
        toast.error("Erro ao enviar mensagem");
      }
      setText(msg); // restore text
      setPendingFile(file);
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      return;
    }

    if (res?.result) {
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m._id !== tempId);
        if (withoutTemp.some((m) => m._id === res.result._id)) return withoutTemp;
        return [...withoutTemp, res.result];
      });
      requestAnimationFrame(() => scrollToBottom());
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handlePickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = "";
  }

  /** Arrastar e soltar na área da conversa: 1 arquivo por mensagem (limites da Cloud API do WhatsApp). */
  function handleDroppedFiles(files: File[]) {
    if (files.length > 1) toast.error("Só é possível enviar 1 arquivo por mensagem; usando o primeiro.");
    const [file] = files;
    const error = validateAttachment(file, "whatsapp");
    if (error) return toast.error(error);
    setPendingFile(file);
  }
  const { isDragging, dropProps } = useFileDrop(handleDroppedFiles, isRecording || sending);

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const item = Array.from(e.clipboardData.items).find((it) => it.type.startsWith("image/"));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    setPendingFile(file);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      recordChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        recordStreamRef.current = null;
        const blob = new Blob(recordChunksRef.current, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
        setPendingFile(file);
      };
      mr.start(200);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch {
      toast.error("Não foi possível acessar o microfone");
    }
  }

  function stopRecording() {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  const selectedIdsArray = Array.from(selectedIds);

  return (
    <div className="relative flex h-full flex-col" {...dropProps}>
      <FileDropOverlay visible={isDragging} label="Solte para anexar à mensagem" />
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="lg:hidden">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-green-100 text-green-700 text-sm">
              {getInitials(conversation.name || conversation.waId)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-sm leading-tight">
              {contact?.name || conversation.name || conversation.waId || "Contato"}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {contact?.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 shrink-0" /> {contact.phone}
                </span>
              )}
              {contact?.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3 shrink-0" /> {contact.email}
                </span>
              )}
              {contact?.companyName && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3 shrink-0" /> {contact.companyName}
                </span>
              )}
              {expired && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock className="h-3 w-3" /> Janela de 24h expirada
                </span>
              )}
            </div>
            {!!contact?.tags?.length && (
              <div className="mt-1 flex flex-wrap gap-1">
                {contact.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {selectionMode ? (
            <>
              <span className="text-xs text-muted-foreground mr-2">
                {selectedIds.size} selecionada(s)
              </span>
              {selectedIds.size > 0 && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs"
                    onClick={() => setCreateTicketOpen(true)}
                  >
                    <TicketIcon className="h-3.5 w-3.5" />
                    Criar ticket
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs"
                    onClick={() => setLinkTicketOpen(true)}
                  >
                    <Link className="h-3.5 w-3.5" />
                    Vincular
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectionMode(false);
                  setSelectedIds(new Set());
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-xs"
                disabled={startingCall}
                onClick={() => void handleStartCall()}
              >
                {startingCall ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Phone className="h-3.5 w-3.5" />
                )}
                Ligar
              </Button>
              {conversation.type === "individual" && !conversation.contactHasAccount && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-xs"
                  disabled={sendingClaimLink}
                  onClick={() => void handleSendClaimLink()}
                >
                  {sendingClaimLink ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="h-3.5 w-3.5" />
                  )}
                  Enviar cadastro
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-xs"
                onClick={() => setSelectionMode(true)}
              >
                <FileText className="h-3.5 w-3.5" />
                Selecionar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden space-y-3 p-4 bg-secondary/20"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-center text-muted-foreground">
            <MessageCircle className="h-10 w-10" />
            <p className="text-sm">Nenhuma mensagem ainda</p>
          </div>
        ) : (
          <>
            {loadingMore && (
              <div className="flex justify-center py-1">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {messages.map((msg) => (
              <MessageBubble
                key={msg._id}
                message={msg}
                quoted={msg.contextWamid ? messagesByWamid.get(msg.contextWamid) : undefined}
                selected={selectedIds.has(msg._id)}
                selectionMode={selectionMode}
                onToggleSelect={toggleSelect}
                onReply={setReplyingTo}
                agentName={
                  msg.senderAgentId ? orgAgents.find((a) => a._id === msg.senderAgentId)?.name : undefined
                }
                seenBy={seenByMessageId.get(msg._id)}
              />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-background p-3">
        {showTemplatePicker ? (
          <TemplatePicker
            conversationId={conversation._id}
            customerName={conversation.name}
            expired={expired}
            onClose={() => setManualTemplateOpen(false)}
            onSent={(message) => {
              setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
              requestAnimationFrame(() => scrollToBottom());
            }}
          />
        ) : (
          <div className="space-y-2">
            {replyingTo && (
              <div className="flex items-start justify-between gap-2 rounded-md border-l-2 border-primary bg-muted px-3 py-1.5 text-xs">
                <p className="min-w-0 whitespace-pre-wrap break-words text-muted-foreground">
                  Respondendo: <span className="text-foreground">{replyingTo.content.msg || "(anexo)"}</span>
                </p>
                <button type="button" onClick={() => setReplyingTo(null)} className="shrink-0 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {pendingFile && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted px-3 py-1.5 text-xs">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {pendingFile.type.startsWith("image/") && pendingFileUrl && (
                    <img src={pendingFileUrl} alt={pendingFile.name} className="h-10 w-10 shrink-0 rounded object-cover" />
                  )}
                  {pendingFile.type.startsWith("audio/") && pendingFileUrl && (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <audio controls src={pendingFileUrl} className="h-8 max-w-[220px]" />
                  )}
                  {!pendingFile.type.startsWith("image/") && !pendingFile.type.startsWith("audio/") && (
                    <>
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{pendingFile.name}</span>
                    </>
                  )}
                </div>
                <button type="button" onClick={() => setPendingFile(null)} className="shrink-0 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <RadioGroup
              value={messageType}
              onValueChange={(v) => setMessageType(v as "public" | "internal")}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="public" id="msg-public" />
                <label
                  htmlFor="msg-public"
                  className={cn("text-sm", messageType === "internal" && "text-muted-foreground")}
                >
                  Mensagem (WhatsApp)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="internal" id="msg-internal" />
                <label
                  htmlFor="msg-internal"
                  className={cn(
                    "text-sm",
                    messageType === "internal" ? "font-medium text-amber-600" : "text-muted-foreground"
                  )}
                >
                  Nota interna (não vai pro cliente)
                </label>
              </div>
            </RadioGroup>
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handlePickFile}
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="shrink-0"
                disabled={sending || isRecording}
                onClick={() => fileInputRef.current?.click()}
                title="Anexar arquivo"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant={isRecording ? "destructive" : "outline"}
                className="shrink-0"
                disabled={sending}
                onClick={() => (isRecording ? stopRecording() : void startRecording())}
                title={isRecording ? "Parar gravação" : "Gravar áudio"}
              >
                {isRecording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="shrink-0"
                disabled={sending || isRecording}
                onClick={() => setManualTemplateOpen(true)}
                title="Enviar template"
              >
                <LayoutTemplate className="h-4 w-4" />
              </Button>
              <MentionTextarea
                ref={textareaRef}
                agents={orgAgents}
                placeholder={messageType === "internal" ? "Escreva a nota interna..." : "Digite sua mensagem..."}
                value={text}
                onValueChange={setText}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={1}
                className={cn(
                  "min-h-[40px] max-h-32 resize-none overflow-y-auto",
                  messageType === "internal" &&
                    "border-amber-300 bg-amber-50 placeholder:text-amber-700/60 dark:border-amber-800 dark:bg-amber-950/40"
                )}
                disabled={sending || isRecording}
              />
              <Button
                size="icon"
                onClick={handleSend}
                variant={messageType === "internal" ? "secondary" : "default"}
                className={cn(
                  "shrink-0",
                  messageType === "internal" && "bg-amber-600 text-white hover:bg-amber-700"
                )}
                disabled={(!text.trim() && !pendingFile) || sending || isRecording}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateTicketModal
        open={createTicketOpen}
        onClose={() => setCreateTicketOpen(false)}
        conversationId={conversation._id}
        messageIds={selectedIdsArray}
        onSuccess={(ticketId) => {
          navigate(`/tickets/${ticketId}`);
        }}
      />
      <LinkTicketModal
        open={linkTicketOpen}
        onClose={() => setLinkTicketOpen(false)}
        conversationId={conversation._id}
        messageIds={selectedIdsArray}
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Conversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showThread, setShowThread] = useState(false); // mobile state
  const [whatsappConfigured, setWhatsappConfigured] = useState<boolean | null>(null);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const navigate = useNavigate();
  // Lido via ref (não como dep do fetchConversations) pra não recriar a função a cada troca de conversa.
  const selectedConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?._id ?? null;
  }, [selectedConversation]);
  const { id: conversationIdParam } = useParams<{ id?: string }>();

  // Connect realtime
  useEffect(() => {
    const token = localStorage.getItem("auth_token") ?? "";
    realtimeService.connect(token);

    const unsub = realtimeService.subscribe("conversation:updated", (ev) => {
      setConversations((prev) =>
        prev.map((c) =>
          c._id === ev.conversationId
            ? {
                ...c,
                lastMessagePreview: ev.lastMessagePreview ?? c.lastMessagePreview,
                lastMessageAt: ev.lastMessageAt ?? c.lastMessageAt,
              }
            : c
        )
      );
    });

    const unsubMsg = realtimeService.subscribe("conversation:message", (ev) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === ev.conversationId);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          lastMessageAt: ev.message.createdAt,
          lastMessagePreview: ev.message.content.msg ?? updated[idx].lastMessagePreview,
          unreadCount:
            selectedConversation?._id === ev.conversationId
              ? 0
              : (updated[idx].unreadCount ?? 0) + 1,
        };
        // Bubble to top
        const [item] = updated.splice(idx, 1);
        updated.unshift(item);
        return updated;
      });
    });

    return () => {
      unsub();
      unsubMsg();
    };
  }, [selectedConversation]);

  const fetchConversations = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setLoading(true);
      const [data] = await conversationService.list({
        page: 1,
        limit: 50,
        ...(search ? { search } : {}),
      });
      if (!opts.silent) setLoading(false);
      if (data?.result) {
        const sorted = [...data.result].sort((a, b) => {
          if (!a.lastMessageAt) return 1;
          if (!b.lastMessageAt) return -1;
          return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
        });
        // A conversa aberta é marcada como lida em tempo real (ver ConversationDetailPanel) — não deixa
        // esse refetch (polling/foco de aba) trazer de volta um unreadCount desatualizado pra ela.
        const openId = selectedConversationIdRef.current;
        setConversations(openId ? sorted.map((c) => (c._id === openId ? { ...c, unreadCount: 0 } : c)) : sorted);
      }
    },
    [search]
  );

  useEffect(() => {
    void fetchConversations();
  }, [fetchConversations]);

  // Fallback de polling: garante que a lista reflita mensagens novas mesmo se o socket
  // cair silenciosamente (rede instável, aba em segundo plano por muito tempo, etc.).
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === "visible") {
        void fetchConversations({ silent: true });
      }
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        void fetchConversations({ silent: true });
      }
    }, 25000);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchConversations]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Verifica se o WhatsApp já está configurado. Em caso de erro, não bloqueia a tela.
  useEffect(() => {
    void (async () => {
      const [data, err] = await whatsappService.status();
      setWhatsappConfigured(err ? true : !!data?.configured);
    })();
  }, []);

  function handleSelectConversation(c: Conversation) {
    setSelectedConversation(c);
    setShowThread(true);
    // Clear unread in local state
    setConversations((prev) =>
      prev.map((conv) => (conv._id === c._id ? { ...conv, unreadCount: 0 } : conv))
    );
    // Reflete na URL — compartilhar o link já abre direto nessa conversa.
    navigate(`/conversations/${c._id}`, { replace: true });
  }

  // Deep-link: se a URL já vem com um id (compartilhado por outro agente, ex.), abre
  // essa conversa direto. Busca na lista já carregada; se não estiver lá (conversa mais
  // antiga, fora da página inicial), busca ela sozinha e adiciona no topo da lista.
  useEffect(() => {
    if (!conversationIdParam || selectedConversation?._id === conversationIdParam) return;
    const inList = conversations.find((c) => c._id === conversationIdParam);
    if (inList) {
      setSelectedConversation(inList);
      setShowThread(true);
      return;
    }
    if (loading) return; // espera a lista inicial carregar antes de decidir que não achou
    void (async () => {
      const [data] = await conversationService.getById(conversationIdParam);
      if (!data?._id) return;
      setConversations((prev) => (prev.some((c) => c._id === data._id) ? prev : [data, ...prev]));
      setSelectedConversation(data);
      setShowThread(true);
    })();
  }, [conversationIdParam, conversations, loading, selectedConversation]);

  // WhatsApp ainda não configurado: explica e leva direto à configuração.
  if (whatsappConfigured === false) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <MessageCircle className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">WhatsApp não configurado</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Para receber e responder conversas você precisa conectar um número do WhatsApp
            Business. Configure a integração para começar a atender por aqui.
          </p>
          <Button className="mt-6 gap-2" onClick={() => navigate("/whatsapp-settings")}>
            <Send className="h-4 w-4" />
            Configurar WhatsApp
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full -m-6 -mb-24 lg:-mb-6">
      {/* Left panel — conversation list */}
      <div
        className={cn(
          "flex flex-col border-r border-border bg-background",
          "w-full lg:w-80 xl:w-96 shrink-0",
          showThread && "hidden lg:flex"
        )}
      >
        <div className="border-b border-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold text-base">Conversas</h2>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5 px-2"
              onClick={() => setNewConversationOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Nova
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar conversas..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-1 p-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground">
              <MessageCircle className="h-8 w-8" />
              <p className="text-sm">
                {search ? "Nenhuma conversa encontrada" : "Nenhuma conversa ainda"}
              </p>
            </div>
          ) : (
            conversations.map((c) => (
              <ConversationItem
                key={c._id}
                conversation={c}
                selected={selectedConversation?._id === c._id}
                onClick={() => handleSelectConversation(c)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right panel — thread */}
      <div
        className={cn(
          "flex-1 bg-background",
          !showThread && "hidden lg:flex",
          "flex flex-col"
        )}
      >
        {selectedConversation ? (
          <ThreadPanel
            conversation={selectedConversation}
            onBack={() => {
              setShowThread(false);
              navigate("/conversations", { replace: true });
            }}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12" />
            <p className="font-medium">Selecione uma conversa</p>
            <p className="text-sm max-w-xs">
              Clique em uma conversa à esquerda para ver as mensagens.
            </p>
          </div>
        )}
      </div>

      <NewConversationModal
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        onSuccess={(conv) => {
          setConversations((prev) => [conv, ...prev.filter((c) => c._id !== conv._id)]);
          handleSelectConversation(conv);
        }}
      />
    </div>
  );
}
